// ===================================
// 2. Master_LoginUserService.ts - ユーザー管理サービス
// ===================================

import {
  collection,
  doc,
  setDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  type DocumentData,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/firebase';
import {
  LoginUser,
  CreateUserRequest,
  UpdateUserRequest,
  UserRole,
  STANDARD_ROLE,
} from './web_parts/types';

/** Firestore の updateDoc に渡すオブジェクトから undefined を除去する */
function omitUndefined<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Record<
    string,
    unknown
  >;
}


/**
 * ユーザー管理サービス
 * Firebase AuthenticationとFirestore loginusersコレクションを連携管理
 */
export class LoginUserService {
  private static getRegionFunctions() {
    return getFunctions(undefined, "asia-northeast1");
  }

  private static normalizeRole(rawRole: unknown): UserRole {
    if (rawRole === "admin") {
      return "admin";
    }
    return STANDARD_ROLE;
  }

  private static normalizeUser(data: Record<string, any>, id: string): LoginUser {
    return {
      id,
      userId: data.userId ?? "",
      displayName: data.displayName ?? data.userName ?? "",
      email: data.email ?? "",
      role: this.normalizeRole(data.role),
      createdAt: data.createdAt ?? Date.now(),
      updatedAt: data.updatedAt,
      lastLoginAt: data.lastLoginAt ?? null,
      phoneNumber: data.phoneNumber ?? "",
      Others: data.Others ?? "",
    };
  }

  /**
   * 新規ユーザーを作成（Firebase Auth + Firestore同時作成）
   * @param userData 作成するユーザー情報
   * @returns 作成されたユーザー情報
   */
// Functions経由で作成するようにしたので、この機能は使用しなくなった。

/*  
  static async createUser(userData: CreateUserRequest): Promise<LoginUser> {
    const auth = getAuth();
    
    try {
      console.log('新規ユーザー作成開始:', userData.email);
      
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        userData.email, 
        userData.password
      );
      
      const firebaseUser = userCredential.user;
      
      await updateProfile(firebaseUser, {
        displayName: userData.displayName
      });
      
      const loginUserData: Omit<LoginUser, 'id' | 'departmentId'> = {
        email: userData.email,
        displayName: userData.displayName,
        role: userData.role,
        createdAt: Date.now(),
        isActive: true,
        companyId: userData.companyId || '',
        orgID: userData.orgID || '',
        orgName: userData.orgName || '',
        Others: userData.Others || ''
      };
      
      await setDoc(doc(db, 'loginusers', firebaseUser.uid), loginUserData);
      
      return { id: firebaseUser.uid, ...loginUserData };
      
    } catch (error) {
      console.error('ユーザー作成エラー:', error);
      
      if (auth.currentUser && auth.currentUser.email === userData.email) {
        try {
          await deleteUser(auth.currentUser);
        } catch (cleanupError) {
          console.error('クリーンアップエラー:', cleanupError);
        }
      }
      
      throw error;
    }
  }
*/

  /**
   * ユーザー一覧を取得
   * @returns ユーザー一覧
   */
  static async getUsers(): Promise<LoginUser[]> {
    try {
      const usersQuery = query(
        collection(db, 'loginusers'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(usersQuery);
      
      return querySnapshot.docs.map((docSnapshot) =>
        this.normalizeUser(docSnapshot.data() as Record<string, any>, docSnapshot.id)
      );
      
    } catch (error) {
      console.error('ユーザー一覧取得エラー:', error);
      throw error;
    }
  }

  /**
   * ユーザー情報を更新（Firestoreのみ）
   * @param userId ユーザーID
   * @param updateData 更新データ
   */
  static async updateUser(userId: string, updateData: UpdateUserRequest): Promise<void> {
    try {
      const userRef = doc(db, 'loginusers', userId);
      const now = Date.now();
      const payload = { ...updateData } as UpdateUserRequest & { email?: string };
      if (typeof payload.email === "string") {
        payload.email = payload.email.trim().toLowerCase();
      }
      await updateDoc(
        userRef,
        omitUndefined({
          ...payload,
          updatedAt: now,
        }) as DocumentData
      );
      
    } catch (error) {
      console.error('ユーザー情報更新エラー:', error);
      throw error;
    }
  }


  /**
   * 新規ユーザーを作成（Cloud Function経由でAuth作成 + Firestoreへデータ保存）
   * @param userData 作成するユーザー情報
   * @returns 作成されたユーザー情報
   */
  static async createUser(userData: CreateUserRequest): Promise<LoginUser> {
    try {
      console.log('新規ユーザー作成開始 (Cloud Function経由):', userData.email);

      // 1. Cloud Functionを呼び出してFirebase Authにユーザーを作成
      const functions = this.getRegionFunctions();
      const createAuthUserFn = httpsCallable(functions, 'createAuthUser');
      const normalizedEmail = userData.email.trim().toLowerCase();
      const result = await createAuthUserFn({
        email: normalizedEmail,
        password: userData.password,
        displayName: userData.displayName ?? normalizedEmail,
      });

      const { uid } = (result.data as any);
      if (!uid) {
        throw new Error('Cloud FunctionからのUID取得に失敗しました。');
      }

      console.log('Authユーザー作成成功, UID:', uid);

      // 2. Firestoreのloginusersコレクションにドキュメントを作成
      //    パスワードは保存しない
      const now = Date.now();
      const resolvedDisplayName = userData.displayName ?? normalizedEmail;
      const loginUserData: Omit<LoginUser, 'id'> = {
        userId: uid,
        displayName: resolvedDisplayName,
        email: normalizedEmail,
        role: this.normalizeRole(userData.role),
        createdAt: now,
        updatedAt: now,
        lastLoginAt: null,
        phoneNumber: userData.phoneNumber ?? "",
        Others: userData.Others || '',
      };
      
      // ドキュメントIDをAuthのUIDと一致させる
      await setDoc(doc(db, 'loginusers', uid), loginUserData);
      console.log('Firestoreにユーザーデータ保存完了');

      return { id: uid, ...loginUserData };
      
    } catch (error) {
      // ★ 修正点: Cloud Functionからの詳細なエラーメッセージを抽出する
      const firebaseError = error as { code?: string; message: string };
      const errorMessage = firebaseError.message || '不明なエラーが発生しました。';
      
      console.error('ユーザー作成エラー:', errorMessage, 'Code:', firebaseError.code);
      
      // UI側で表示するために、抽出したメッセージで新しいErrorをスローする
      throw new Error(errorMessage);
    }
 }

  /**
   * ユーザーのパスワードを更新 (Cloud Function経由)
   * @param userId 対象のユーザーID
   * @param newPassword 新しいパスワード
   */
  static async updateUserPassword(userId: string, newPassword: string): Promise<void> {
    try {
      const functions = this.getRegionFunctions();
      const updateUserPasswordFn = httpsCallable(functions, 'updateUserPassword');
      
      const result = await updateUserPasswordFn({ userId, newPassword });

      if (!(result.data as any).success) {
        throw new Error((result.data as any).message || 'パスワードの更新に失敗しました。');
      }

    } catch (error) {
      console.error('パスワード更新エラー:', error);
      // UIにエラーメッセージを伝えるために、エラーを再スローする
      throw error; 
    }
  }

  /**
   * ユーザーを完全削除（Firestore loginusers + Firebase Auth）
   * ※ contract（管理者セットアップ履歴）は保持
   */
  static async deleteUser(userId: string): Promise<void> {
    try {
      const functions = this.getRegionFunctions();
      const deleteLoginUserFn = httpsCallable(functions, "deleteLoginUser");
      const result = await deleteLoginUserFn({ userId });
      if (!(result.data as any)?.success) {
        throw new Error((result.data as any)?.message || "ユーザー削除に失敗しました。");
      }
    } catch (error) {
      console.error("ユーザー削除エラー:", error);
      throw error;
    }
  }

}
